/**
 * Reads `skidaCrmMentions`. Read-only — written by the collector.
 *
 * Ordered by the post's own timestamp rather than by when we noticed it, since
 * "what happened lately" is the question this answers. That field is a plain
 * ISO string from Instagram, so the sort is done here rather than in a query —
 * it saves an index for a collection that will hold tens of rows.
 */
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { CRM_COLLECTIONS } from '@/shared/firebase/collections'
import { toIsoOrNull } from '@/shared/util/dates'
import type { Mention, MentionMatchKind } from '@/types/mentions'

const str = (v: unknown) => (typeof v === 'string' ? v : '')
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null)


const KINDS: MentionMatchKind[] = ['ambassador', 'partner', 'lead', 'event']

export function subscribeToMentions(
  onData: (mentions: Mention[]) => void,
  onError: (e: Error) => void,
): () => void {
  return onSnapshot(
    query(collection(db, CRM_COLLECTIONS.mentions)),
    (snap) => {
      const rows = snap.docs.map((d): Mention => {
        const v = d.data()
        const kind = str(v.matchKind)
        return {
          id: d.id,
          username: str(v.username),
          postedAt: str(v.postedAt) || null,
          permalink: str(v.permalink),
          caption: str(v.caption),
          mediaType: str(v.mediaType),
          productType: str(v.productType),
          thumbnailUrl: str(v.thumbnailUrl),
          likeCount: num(v.likeCount),
          commentCount: num(v.commentCount),
          matchKind: KINDS.includes(kind as MentionMatchKind)
            ? (kind as MentionMatchKind)
            : null,
          matchId: str(v.matchId),
          matchName: str(v.matchName),
          conflict: str(v.conflict),
          seenAt: toIsoOrNull(v.seenAt),
        }
      })
      rows.sort((a, b) => (b.postedAt ?? '').localeCompare(a.postedAt ?? ''))
      onData(rows)
    },
    (e) => onError(e as Error),
  )
}

/**
 * Every tag from one account, newest first.
 *
 * Queried by handle rather than by the collector's stored `matchId`, so a
 * record that has only just been given its handle shows its history
 * immediately instead of after the next nightly run.
 */
export async function mentionsForHandle(handle: string): Promise<Mention[]> {
  const key = handle.trim().toLowerCase().replace(/^@/, '')
  if (!key) return []

  const snap = await getDocs(
    query(collection(db, CRM_COLLECTIONS.mentions), where('username', '==', key)),
  )
  const rows = snap.docs.map((d): Mention => {
    const v = d.data()
    return {
      id: d.id,
      username: str(v.username),
      postedAt: str(v.postedAt) || null,
      permalink: str(v.permalink),
      caption: str(v.caption),
      mediaType: str(v.mediaType),
      productType: str(v.productType),
      thumbnailUrl: str(v.thumbnailUrl),
      likeCount: num(v.likeCount),
      commentCount: num(v.commentCount),
      matchKind: null,
      matchId: str(v.matchId),
      matchName: str(v.matchName),
      conflict: str(v.conflict),
      seenAt: toIsoOrNull(v.seenAt),
    }
  })
  rows.sort((a, b) => (b.postedAt ?? '').localeCompare(a.postedAt ?? ''))
  return rows
}
